import {
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  MenuBook as MenuBookIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  PersonSearch as PersonSearchIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  Star as StarIcon,
  Translate as TranslateIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { doc, setDoc } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CancelButton from '../components/CancelButton';
import Loading from '../components/Loading';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase';
import { clientApi } from '../services/clientApi';
import { clientRequestApi } from '../services/clientRequestApi';
import { Dietitian, dietitianApi } from '../services/dietitianApi';
import { ratingApi } from '../services/ratingApi';
import { colors } from '../theme';

const SelectDietitianPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [dietitians, setDietitians] = useState<Dietitian[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [requestMessage, setRequestMessage] = useState<string>('');
  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [selectedDietitianId, setSelectedDietitianId] = useState<string | null>(
    null
  );
  const [clientRequests, setClientRequests] = useState<Map<string, string>>(
    new Map()
  );
  const [profileDetailDietitian, setProfileDetailDietitian] = useState<Dietitian | null>(null);
  const [ratingSummaries, setRatingSummaries] = useState<Map<string, { averageScore: number; totalCount: number }>>(new Map());

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Client ID'yi al
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        navigate('/client/login');
        return;
      }

      try {
        const client = await clientApi.getClientByEmail(userEmail);
        setClientId(client.id);

        // Eğer zaten bir diyetisyeni varsa (dietitianId veya dietitianEmail) dashboard'a yönlendir
        // Bu, diyetisyen tarafından eklenmiş danışanlar için geçerlidir
        if (client.dietitianId || client.dietitianEmail) {
          console.log(
            "Danışan zaten bir diyetisyene bağlı, dashboard'a yönlendiriliyor..."
          );
          navigate('/client');
          return;
        }
      } catch (err) {
        console.error('Client yüklenemedi:', err);
        setError('Danışan bilgileri yüklenemedi. Lütfen tekrar giriş yapın.');
        return;
      }

      // Online danışan alan diyetisyenleri getir
      const onlineDietitians = await dietitianApi.getOnlineDietitians();
      setDietitians(onlineDietitians);

      // Her diyetisyen için puan özetini çek (danışan seçerken görsün)
      const summaryList = await Promise.all(
        onlineDietitians.map((d) =>
          ratingApi.getSummary(d.id).catch(() => ({ averageScore: 0, totalCount: 0 }))
        )
      );
      const summariesMap = new Map<string, { averageScore: number; totalCount: number }>();
      onlineDietitians.forEach((d, i) => summariesMap.set(d.id, summaryList[i]));
      setRatingSummaries(summariesMap);

      // Mevcut istekleri getir
      if (clientId) {
        try {
          const requests = await clientRequestApi.getClientRequestsByClient(
            clientId
          );
          const requestsMap = new Map<string, string>();
          requests.forEach((req) => {
            requestsMap.set(req.dietitianId, req.status);
          });
          setClientRequests(requestsMap);
        } catch (err) {
          console.error('İstekler yüklenemedi:', err);
        }
      }
    } catch (err: any) {
      console.error('Diyetisyenler yüklenemedi:', err);
      setError(err.message || 'Diyetisyenler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenRequestDialog = (dietitianId: string) => {
    setSelectedDietitianId(dietitianId);
    setRequestMessage('');
    setOpenRequestDialog(true);
  };

  const handleCloseRequestDialog = () => {
    setOpenRequestDialog(false);
    setSelectedDietitianId(null);
    setRequestMessage('');
  };

  const handleSendRequest = async () => {
    if (!clientId || !selectedDietitianId) {
      showError('Danışan bilgisi bulunamadı.', 'Hata');
      return;
    }

    try {
      setSendingRequest(selectedDietitianId);
      await clientRequestApi.createClientRequest({
        clientId,
        dietitianId: selectedDietitianId,
        message: requestMessage.trim() || undefined,
      });
      showSuccess('İstek başarıyla gönderildi!', 'Başarılı');

      // İstek durumunu güncelle
      const newRequestsMap = new Map(clientRequests);
      newRequestsMap.set(selectedDietitianId, 'Pending');
      setClientRequests(newRequestsMap);

      handleCloseRequestDialog();
    } catch (err: any) {
      console.error('İstek gönderilemedi:', err);
      showError(err.message || 'İstek gönderilirken bir hata oluştu.', 'Hata');
    } finally {
      setSendingRequest(null);
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleOpenMessage = async (dietitian: Dietitian) => {
    if (!clientId) {
      showError('Danışan bilgisi bulunamadı.', 'Hata');
      return;
    }

    try {
      const userEmail = localStorage.getItem('userEmail') || '';
      
      // Conversation ID oluştur (her zaman aynı sırada)
      let conversationId = `${dietitian.id}_${clientId}`;
      if (dietitian.id > clientId) {
        conversationId = `${clientId}_${dietitian.id}`;
      }

      // Conversation'ı oluştur veya güncelle
      // Bu conversation henüz aktif bir danışan ilişkisi olmadığı için isActiveClient: false
      const conversationRef = doc(db, 'conversations', conversationId);
      await setDoc(
        conversationRef,
        {
          dietitianId: dietitian.id,
          clientId: clientId,
          dietitianName: dietitian.email || `${dietitian.firstName} ${dietitian.lastName}`,
          clientName: userEmail,
          lastMessage: '',
          lastMessageTime: new Date(),
          unreadCount: 0,
          isActiveClient: false, // Henüz aktif danışan değil, sadece mesaj gönderen
        },
        { merge: true } // Varsa güncelle, yoksa oluştur
      );

      // Mesaj sayfasına yönlendir ve conversation'ı aç
      navigate(`/client/messages?conversation=${conversationId}`);
    } catch (err: any) {
      console.error('Mesaj açılırken hata:', err);
      showError(err.message || 'Mesaj açılırken bir hata oluştu.', 'Hata');
    }
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Diyetisyenler yükleniyor..."
      />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1400px',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          flex={1}
          minWidth={0}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PersonSearchIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box minWidth={0} flex={1}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: colors.text.primary }}
              noWrap
            >
              Diyetisyen Seçin
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Online danışan alan diyetisyenlerden birini seçerek iletişime
              geçebilir, diyet programlarınızı takip edebilirsiniz.
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {dietitians.length === 0 ? (
        <Card
          elevation={1}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: 'white',
            border: `1px solid ${colors.border.light}`,
          }}
        >
          <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Henüz online danışan alan diyetisyen bulunmuyor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Lütfen daha sonra tekrar deneyin.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {dietitians.map((dietitian) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={dietitian.id}>
              <Card
                elevation={2}
                onClick={() => setProfileDetailDietitian(dietitian)}
                sx={{
                  height: 380,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: `1px solid ${colors.border.light}`,
                  transition: 'all 0.2s ease',
                  bgcolor: colors.background.paper,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    borderColor: colors.primary.light,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3.5, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 3,
                      gap: 2,
                    }}
                  >
                    <Avatar
                      src={dietitian.profileImageUrl}
                      sx={{
                        width: 72,
                        height: 72,
                        bgcolor: colors.primary.main,
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        border: '3px solid',
                        borderColor: colors.primary.light,
                        boxShadow: '0 4px 12px rgba(67, 160, 71, 0.2)',
                      }}
                    >
                      {getInitials(dietitian.firstName, dietitian.lastName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ mb: 0.5 }}
                          noWrap
                          flex={1}
                        >
                          {dietitian.firstName} {dietitian.lastName}
                        </Typography>
                        <Tooltip title="Mesaj Gönder">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMessage(dietitian);
                            }}
                            sx={{
                              color: colors.primary.main,
                              width: 'fit-content',
                              '&:hover': {
                                bgcolor: colors.alpha.primary10,
                              },
                            }}
                          >
                            <MessageIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {dietitian.specialization && (
                        <Chip
                          label={dietitian.specialization}
                          size="small"
                          sx={{
                            mt: 0.5,
                            bgcolor: colors.alpha.primary10,
                            color: colors.primary.dark,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            height: 24,
                            border: `1px solid ${colors.alpha.primary20}`,
                          }}
                        />
                      )}
                      {/* Puan: diyetisyenin altında görsel (yıldız + ortalama · oy sayısı) — her zaman göster */}
                      {(() => {
                        const summary = ratingSummaries.get(dietitian.id);
                        const avg = summary?.averageScore ?? 0;
                        const count = summary?.totalCount ?? 0;
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              mt: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Rating
                              value={avg}
                              readOnly
                              size="small"
                              precision={0.1}
                              icon={<StarIcon sx={{ fontSize: 18, color: colors.primary.main }} />}
                              emptyIcon={<StarIcon sx={{ fontSize: 18, color: '#dbdbdb' }} />}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              {count > 0 ? `${avg.toFixed(1)} · ${count} oy` : 'Henüz oy yok'}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>

                  {dietitian.bio && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      {dietitian.bio}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: colors.background.light,
                      border: `1px solid ${colors.border.light}`,
                      flexShrink: 0,
                    }}
                  >
                    {dietitian.email && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                          color: 'text.secondary',
                        }}
                      >
                        <EmailIcon
                          sx={{
                            fontSize: 20,
                            mr: 1.5,
                            color: colors.primary.main,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            wordBreak: 'break-word',
                          }}
                        >
                          {dietitian.email}
                        </Typography>
                      </Box>
                    )}
                    {dietitian.phone && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: 'text.secondary',
                        }}
                      >
                        <PhoneIcon
                          sx={{
                            fontSize: 20,
                            mr: 1.5,
                            color: colors.primary.main,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {dietitian.phone}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{ mt: 'auto', flexShrink: 0 }}
                  >
                  {(() => {
                    const requestStatus = clientRequests.get(dietitian.id);
                    if (requestStatus === 'Pending') {
                      return (
                        <Chip
                          label="İstek Beklemede"
                          sx={{
                            width: '100%',
                            py: 1.5,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            bgcolor: colors.status.warning.background,
                            color: colors.status.warning.main,
                            border: `1px solid ${colors.status.warning.border}`,
                          }}
                        />
                      );
                    }
                    if (requestStatus === 'Accepted') {
                      return (
                        <Chip
                          label="İstek Kabul Edildi"
                          sx={{
                            width: '100%',
                            py: 1.5,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            bgcolor: colors.alpha.primary10,
                            color: colors.status.success,
                            border: `1px solid ${colors.alpha.primary20}`,
                          }}
                        />
                      );
                    }
                    if (requestStatus === 'Rejected') {
                      return (
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => handleOpenRequestDialog(dietitian.id)}
                          disabled={
                            sendingRequest === dietitian.id || !!sendingRequest
                          }
                          sx={{
                            mt: 'auto',
                            py: 1.5,
                            borderRadius: 2,
                            borderColor: colors.border.light,
                            color: colors.text.primary,
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: colors.primary.main,
                              bgcolor: colors.alpha.primary05,
                              color: colors.primary.main,
                            },
                          }}
                        >
                          Tekrar İstek Gönder
                        </Button>
                      );
                    }
                    return (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleOpenRequestDialog(dietitian.id)}
                        disabled={
                          sendingRequest === dietitian.id || !!sendingRequest
                        }
                        startIcon={
                          sendingRequest === dietitian.id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <CheckCircleIcon />
                          )
                        }
                        sx={{
                          mt: 'auto',
                          py: 1.5,
                          borderRadius: 2,
                          bgcolor: colors.primary.main,
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          boxShadow: '0 2px 8px rgba(67, 160, 71, 0.2)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: colors.hover.primary,
                            boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                            transform: 'translateY(-1px)',
                          },
                          '&:disabled': {
                            bgcolor: colors.primary.main,
                            opacity: 0.6,
                          },
                        }}
                      >
                        {sendingRequest === dietitian.id
                          ? 'Gönderiliyor...'
                          : 'İstek Gönder'}
                      </Button>
                    );
                  })()}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Detaylı Profil Dialog */}
      <Dialog
        open={!!profileDetailDietitian}
        onClose={() => setProfileDetailDietitian(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        {profileDetailDietitian && (
          <>
            <DialogTitle
              sx={{
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Avatar
                src={profileDetailDietitian.profileImageUrl}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: colors.primary.main,
                  fontSize: '1.25rem',
                  fontWeight: 700,
                }}
              >
                {getInitials(profileDetailDietitian.firstName, profileDetailDietitian.lastName)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {profileDetailDietitian.firstName} {profileDetailDietitian.lastName}
                </Typography>
                {profileDetailDietitian.specialization && (
                  <Chip
                    label={profileDetailDietitian.specialization}
                    size="small"
                    sx={{
                      mt: 0.5,
                      bgcolor: colors.alpha.primary10,
                      color: colors.primary.dark,
                      fontSize: '0.75rem',
                      maxWidth: '100%',
                      height: 'auto',
                      minHeight: 24,
                      py: 0.5,
                      alignItems: 'flex-start',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        display: 'block',
                        lineHeight: 1.4,
                      },
                    }}
                  />
                )}
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {(() => {
                const r = ratingSummaries.get(profileDetailDietitian.id);
                const avg = r?.averageScore ?? 0;
                const count = r?.totalCount ?? 0;
                return (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating
                      value={avg}
                      readOnly
                      size="small"
                      precision={0.1}
                      icon={<StarIcon sx={{ fontSize: 20, color: colors.primary.main }} />}
                      emptyIcon={<StarIcon sx={{ fontSize: 20, color: '#dbdbdb' }} />}
                    />
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      {count > 0 ? `${avg.toFixed(1)} · ${count} oy` : 'Henüz oy yok'}
                    </Typography>
                  </Box>
                );
              })()}
              {profileDetailDietitian.bio && (
                <Box sx={{ mb: 3 ,mt:2}}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                    Hakkımda
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {profileDetailDietitian.bio}
                  </Typography>
                </Box>
              )}
              {profileDetailDietitian.education && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SchoolIcon sx={{ fontSize: 18 }} /> Eğitim
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {profileDetailDietitian.education}
                  </Typography>
                </Box>
              )}
              {profileDetailDietitian.certifications && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MenuBookIcon sx={{ fontSize: 18 }} /> Sertifikalar
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {profileDetailDietitian.certifications}
                  </Typography>
                </Box>
              )}
              {(profileDetailDietitian.experienceYears != null && profileDetailDietitian.experienceYears > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WorkIcon sx={{ fontSize: 18 }} /> Deneyim
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {profileDetailDietitian.experienceYears} yıl
                  </Typography>
                </Box>
              )}
              {profileDetailDietitian.languages && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TranslateIcon sx={{ fontSize: 18 }} /> Çalıştığı Diller
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {profileDetailDietitian.languages}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 3, p: 2, bgcolor: colors.background.light, borderRadius: 2 }}>
                {profileDetailDietitian.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <EmailIcon sx={{ fontSize: 20, color: colors.primary.main }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profileDetailDietitian.email}</Typography>
                  </Box>
                )}
                {profileDetailDietitian.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ fontSize: 20, color: colors.primary.main }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profileDetailDietitian.phone}</Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              <CancelButton onClick={() => setProfileDetailDietitian(null)}>Kapat</CancelButton>
              <Button
                variant="contained"
                startIcon={<MessageIcon />}
                onClick={() => {
                  handleOpenMessage(profileDetailDietitian);
                  setProfileDetailDietitian(null);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: colors.primary.main,
                  '&:hover': { bgcolor: colors.hover.primary },
                }}
              >
                Mesaj Gönder
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* İstek Gönderme Dialog */}
      <Dialog
        open={openRequestDialog}
        onClose={handleCloseRequestDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Diyetisyene İstek Gönder
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedDietitianId && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Diyetisyen:
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {
                  dietitians.find((d) => d.id === selectedDietitianId)
                    ?.firstName
                }{' '}
                {dietitians.find((d) => d.id === selectedDietitianId)?.lastName}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Mesajınız (Opsiyonel)"
            placeholder="Diyetisyene iletmek istediğiniz bir mesaj varsa buraya yazabilirsiniz..."
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            İsteğiniz diyetisyene iletilecek ve diyetisyen kabul/red edebilecek.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <CancelButton onClick={handleCloseRequestDialog} />
          <Button
            onClick={handleSendRequest}
            variant="contained"
            disabled={sendingRequest !== null}
            startIcon={
              sendingRequest ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              py: 1,
              borderRadius: 2,
              background: colors.gradients.primary,
              '&:hover': {
                background: colors.gradients.primaryMedium,
              },
            }}
          >
            {sendingRequest ? 'Gönderiliyor...' : 'İstek Gönder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelectDietitianPage;
